// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { createInstance, type InitOptions } from 'i18next';
import LanguageDetector, {
  type DetectorOptions,
} from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { getBrandDomainByHostname } from '@shared/environment';
// Import English translations
import aggregatorEN from '@shared/ui-kit/assets/data/locales/en/aggregator.json';
import authEN from '@shared/ui-kit/assets/data/locales/en/auth.json';
import commonEN from '@shared/ui-kit/assets/data/locales/en/common.json';
import errorsEN from '@shared/ui-kit/assets/data/locales/en/errors.json';
import glossaryEN from '@shared/ui-kit/assets/data/locales/en/glossary.json';
import userEN from '@shared/ui-kit/assets/data/locales/en/user.json';
import validationEN from '@shared/ui-kit/assets/data/locales/en/validation.json';
import componentsEN from '@shared/ui-kit/assets/data/locales/en/components.json';
import popupsEN from '@shared/ui-kit/assets/data/locales/en/popups.json';
import dashboardEN from '@shared/ui-kit/assets/data/locales/en/dashboard.json';
import aiBuilderEN from '@shared/ui-kit/assets/data/locales/en/ai-builder.json';
import rebalanceEN from '@shared/ui-kit/assets/data/locales/en/rebalance.json';
import taxEN from '@shared/ui-kit/assets/data/locales/en/tax.json';
import researchEN from '@shared/ui-kit/assets/data/locales/en/research.json';
import inversisActivitiesEN from '@shared/ui-kit/assets/data/locales/en/inversis/activities.json';
import inversisActivityTypesEN from '@shared/ui-kit/assets/data/locales/en/inversis/activity-types.json';
import inversisCountriesEN from '@shared/ui-kit/assets/data/locales/en/inversis/countries.json';
import inversisDocumentTypesFEN from '@shared/ui-kit/assets/data/locales/en/inversis/document-types-f.json';
import inversisProvincesEN from '@shared/ui-kit/assets/data/locales/en/inversis/provinces.json';
import inversisSectorsEN from '@shared/ui-kit/assets/data/locales/en/inversis/sectors.json';
import inversisViasEN from '@shared/ui-kit/assets/data/locales/en/inversis/vias.json';

// Import Spanish translations
import aggregatorES from '@shared/ui-kit/assets/data/locales/es/aggregator.json';
import authES from '@shared/ui-kit/assets/data/locales/es/auth.json';
import commonES from '@shared/ui-kit/assets/data/locales/es/common.json';
import errorsES from '@shared/ui-kit/assets/data/locales/es/errors.json';
import glossaryES from '@shared/ui-kit/assets/data/locales/es/glossary.json';
import userES from '@shared/ui-kit/assets/data/locales/es/user.json';
import validationES from '@shared/ui-kit/assets/data/locales/es/validation.json';
import componentsES from '@shared/ui-kit/assets/data/locales/es/components.json';
import popupsES from '@shared/ui-kit/assets/data/locales/es/popups.json';
import dashboardES from '@shared/ui-kit/assets/data/locales/es/dashboard.json';
import aiBuilderES from '@shared/ui-kit/assets/data/locales/es/ai-builder.json';
import rebalanceES from '@shared/ui-kit/assets/data/locales/es/rebalance.json';
import taxES from '@shared/ui-kit/assets/data/locales/es/tax.json';
import researchES from '@shared/ui-kit/assets/data/locales/es/research.json';
import inversisActivitiesES from '@shared/ui-kit/assets/data/locales/es/inversis/activities.json';
import inversisActivityTypesES from '@shared/ui-kit/assets/data/locales/es/inversis/activity-types.json';
import inversisCountriesES from '@shared/ui-kit/assets/data/locales/es/inversis/countries.json';
import inversisDocumentTypesFES from '@shared/ui-kit/assets/data/locales/es/inversis/document-types-f.json';
import inversisProvincesES from '@shared/ui-kit/assets/data/locales/es/inversis/provinces.json';
import inversisSectorsES from '@shared/ui-kit/assets/data/locales/es/inversis/sectors.json';
import inversisViasES from '@shared/ui-kit/assets/data/locales/es/inversis/vias.json';

/** Loose typing for Inversis master-data namespaces (large JSON blobs). */
type InversisNamespaceResource = Record<string, unknown>;

/**
 * Represents the resources available for i18next translations. Each property
 * corresponds to a namespace containing the translations for that particular
 * category.
 */
export type I18NextResources = {
  /** Translations for common terms used across the application. */
  common: typeof commonEN;
  /** Translations for error messages. */
  errors: typeof errorsEN;
  /** Translations for glossary terms. */
  glossary: typeof glossaryEN;
  /** Translations for validation messages. */
  validation: typeof validationEN;
  /** Translations for authentication-related terms. */
  auth: typeof authEN;
  /** Translations for user-related terms. */
  user: typeof userEN;
  /** Translations for aggregator-related terms. */
  aggregator: typeof aggregatorEN;
  /** Translations for components within the application. */
  components: typeof componentsEN;
  /** Translations for key performance indicators (KPIs) related terms. */
  popups: typeof popupsEN;
  /** Translations for dashboard-related terms. */
  dashboard: typeof dashboardEN;
  /** Translations for ai-builder-related terms. */
  'ai-builder': typeof aiBuilderEN;
  /** Translations for rebalance-related terms. */
  rebalance: typeof rebalanceEN;
  /** Translations for tax-related terms. */
  tax: typeof taxEN;
  /** Translations for research-related terms. */
  research: typeof researchEN;
  /** Inversis master: activity types (natural person). */
  'inversis-activity-types': InversisNamespaceResource;
  /** Inversis master: activities with sector / work-type relations. */
  'inversis-activities': InversisNamespaceResource;
  /** Inversis master: countries. */
  'inversis-countries': InversisNamespaceResource;
  /** Inversis master: document types (natural person F). */
  'inversis-document-types-f': InversisNamespaceResource;
  /** Inversis master: provinces. */
  'inversis-provinces': InversisNamespaceResource;
  /** Inversis master: economic sectors. */
  'inversis-sectors': InversisNamespaceResource;
  /** Inversis master: street types (vías). */
  'inversis-vias': InversisNamespaceResource;
};

/**
 * Represents a type-safe key for accessing translations within a specific
 * namespace.
 *
 * @template T - The namespace key within I18NextResources.
 */
export type TranslationKey<T extends keyof I18NextResources> =
  keyof I18NextResources[T];

/**
 * Represents a type-safe key for accessing translations within a specific
 * namespace.
 *
 * @template T - The namespace key within I18NextResources.
 */
export type TranslationKeyWithNamespace<T extends keyof I18NextResources> = [
  TranslationKey<T>,
  { ns: T },
];

export type LanguageId = 'en' | 'es';

declare module 'i18next' {
  interface CustomTypeOptions {
    defaultNS: 'common';
    resources: I18NextResources;
    keySeparator: '.';
    returnNull: false;
    returnEmptyString: false;
    returnObjects: true;
  }
}

declare module 'react-i18next' {
  interface CustomTypeOptions {
    defaultNS: 'common';
    resources: I18NextResources;
    keySeparator: '.';
    returnNull: false;
    returnEmptyString: false;
    returnObjects: true;
  }
}

/**
 * This module initializes the i18n instance with necessary plugins and
 * configurations. It imports translations directly from JSON files, uses
 * LanguageDetector for detecting the user's language, and initReactI18next for
 * React bindings.
 *
 * The translations are structured in namespaces including 'common', 'errors',
 * 'glossary', 'validation', 'auth', and 'aggregator'. The supported languages are
 * English ('en') and Spanish ('es') with English as the fallback language.
 *
 * The `TranslationKey` type is provided to ensure type-safe access to the keys of
 * each namespace.
 */
export const i18n = createInstance();

const supportedLngs = ['en', 'es'] as const;

const resources = {
  en: {
    common: commonEN,
    errors: errorsEN,
    glossary: glossaryEN,
    validation: validationEN,
    auth: authEN,
    user: userEN,
    aggregator: aggregatorEN,
    components: componentsEN,
    popups: popupsEN,
    dashboard: dashboardEN,
    'ai-builder': aiBuilderEN,
    rebalance: rebalanceEN,
    tax: taxEN,
    research: researchEN,
    'inversis-activity-types': inversisActivityTypesEN as InversisNamespaceResource,
    'inversis-activities': inversisActivitiesEN as InversisNamespaceResource,
    'inversis-countries': inversisCountriesEN as InversisNamespaceResource,
    'inversis-document-types-f':
      inversisDocumentTypesFEN as InversisNamespaceResource,
    'inversis-provinces': inversisProvincesEN as InversisNamespaceResource,
    'inversis-sectors': inversisSectorsEN as InversisNamespaceResource,
    'inversis-vias': inversisViasEN as InversisNamespaceResource,
  },
  es: {
    common: commonES,
    errors: errorsES,
    glossary: glossaryES,
    validation: validationES,
    auth: authES,
    user: userES,
    aggregator: aggregatorES,
    components: componentsES,
    popups: popupsES,
    dashboard: dashboardES,
    'ai-builder': aiBuilderES,
    rebalance: rebalanceES,
    tax: taxES,
    research: researchES,
    'inversis-activity-types': inversisActivityTypesES as InversisNamespaceResource,
    'inversis-activities': inversisActivitiesES as InversisNamespaceResource,
    'inversis-countries': inversisCountriesES as InversisNamespaceResource,
    'inversis-document-types-f':
      inversisDocumentTypesFES as InversisNamespaceResource,
    'inversis-provinces': inversisProvincesES as InversisNamespaceResource,
    'inversis-sectors': inversisSectorsES as InversisNamespaceResource,
    'inversis-vias': inversisViasES as InversisNamespaceResource,
  },
} as const;

const getBrandI18nVariables = () => {
  if (typeof window === 'undefined') {
    return {
      brand: 'CITEC',
      brandTitle: 'Citec',
      brandCompany: 'Citec Solutions',
      brandAi: 'Citec AI',
    };
  }

  return getBrandDomainByHostname(window.location.hostname) === 'nuvara'
    ? {
        brand: 'Nuvara 360',
        brandTitle: 'Nuvara 360',
        brandCompany: 'Nuvara 360',
        brandAi: 'Nuvara 360 AI',
      }
    : {
        brand: 'CITEC',
        brandTitle: 'Citec',
        brandCompany: 'Citec Solutions',
        brandAi: 'Citec AI',
      };
};

const commonInitOptions: InitOptions<DetectorOptions> = {
  fallbackLng: 'es',
  defaultNS: 'common',
  ns: [
    'common',
    'errors',
    'glossary',
    'validation',
    'auth',
    'user',
    'aggregator',
    'components',
    'popups',
    'dashboard',
    'ai-builder',
    'rebalance',
    'tax',
    'research',
    'inversis-activity-types',
    'inversis-activities',
    'inversis-countries',
    'inversis-document-types-f',
    'inversis-provinces',
    'inversis-sectors',
    'inversis-vias',
  ],
  debug: false,
  interpolation: {
    escapeValue: false, // not needed for react!!
    defaultVariables: getBrandI18nVariables(),
  },
  supportedLngs: supportedLngs,
  resources,
  returnNull: false,
  returnEmptyString: false,
  returnObjects: true,
  detection: {
    order: ['localStorage', 'cookie', 'htmlTag'],
    caches: ['localStorage', 'cookie'],
  },
};

i18n.use(LanguageDetector).use(initReactI18next).init(commonInitOptions);

export const [i18nextEnglishOnly, i18nextSpanishOnly] = supportedLngs.map(lng => {
  const instance = createInstance();

  instance.use(initReactI18next).init({
    ...commonInitOptions,
    lng: lng,
    supportedLngs: [lng],
    fallbackLng: lng,
    resources: {
      [lng]: resources[lng],
    },
  });

  return instance;
});
declare module 'i18next' {
  interface TFunction {
    (
      key: string | string[],
      options?: {
        ns?: keyof I18NextResources;
        [key: string]: any;
      },
    ): any;
  }
}
